<template>
  <div class="article-page">
    <!-- 面包屑 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <span class="current">指标体系建设</span>
    </nav>

    <article class="container">
      <!-- 头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">数据治理知识库</span>
          <span class="tag">指标体系</span>
          <span class="tag">数据标准</span>
        </div>
        <h1 class="article-title">指标体系建设</h1>
        <p class="article-subtitle">企业级指标规划 · 指标定义标准 · 口径统一 · 指标字典 · 血缘管理</p>
        <div class="article-meta">
          <span><i class="far fa-calendar-alt"></i> 2026-01-02</span>
          <span><i class="far fa-clock"></i> 阅读约 15 分钟</span>
          <span><i class="far fa-user"></i> 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读 -->
      <section class="intro-card">
        <div class="intro-icon">📊</div>
        <div>
          <h3>导读</h3>
          <p>指标是企业经营分析和数据驱动决策的核心语言。然而很多企业面临"指标定义不统一、口径各说各话、报表数据打架"的困境。本知识库系统介绍指标体系建设的完整方法论，从分层模型到指标字典，从口径统一到血缘管理，帮助企业建立"一个数、一个口径、一份报表"的指标管理体系。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、为什么要建设指标体系</a></li>
          <li><a href="#section2">二、指标分层模型</a></li>
          <li><a href="#section3">三、指标定义标准</a></li>
          <li><a href="#section4">四、指标口径统一方法论</a></li>
          <li><a href="#section5">五、指标字典模板</a></li>
          <li><a href="#section6">六、指标血缘管理</a></li>
          <li><a href="#section7">七、指标运营与持续优化</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、为什么要建设指标体系</h2>

          <div class="pain-comparison">
            <div class="pain-col before">
              <h4>❌ 没有指标体系时</h4>
              <ul>
                <li>同一个"营收"指标，财务、业务、管理层口径各异</li>
                <li>月报数据和周报数据对不上</li>
                <li>每次做分析都要"先定义指标"</li>
                <li>新人入职3个月还搞不清楚KPI怎么算</li>
                <li>不同报表工具出的数字不一致</li>
              </ul>
            </div>
            <div class="pain-col after">
              <h4>✅ 建立指标体系后</h4>
              <ul>
                <li>全公司对"营收"有唯一统一的计算口径</li>
                <li>所有报表数出一源、数出同门</li>
                <li>指标自助查询，即查即用，定义透明</li>
                <li>新人阅读指标字典即可理解业务</li>
                <li>口径变更有流程、有审计、可追溯</li>
              </ul>
            </div>
          </div>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-number">40%</div>
              <div class="stat-label">的分析师时间花在"对口径"上</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">3套+</div>
              <div class="stat-label">大型企业平均有3套以上重复的KPI定义</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">60%</div>
              <div class="stat-label">的数据治理项目将指标治理作为首要任务</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">5x</div>
              <div class="stat-label">指标体系成熟的企业决策效率提升5倍</div>
            </div>
          </div>
        </section>

        <!-- 第二章 -->
        <section id="section2" class="chapter">
          <h2>二、指标分层模型</h2>
          <p>科学的指标体系遵循"战略-战术-运营"三层金字塔模型，确保指标从上到下对齐、从下到上支撑。</p>

          <div class="pyramid">
            <div class="pyramid-level" v-for="level in pyramidLevels" :key="level.name" :style="{ background: level.color }">
              <div class="pyramid-content">
                <h4>{{ level.name }}</h4>
                <p>{{ level.desc }}</p>
                <div class="pyramid-examples">
                  <span v-for="(ex, i) in level.examples" :key="i">{{ ex }}</span>
                </div>
              </div>
            </div>
          </div>

          <h3>指标分类维度</h3>
          <div class="classify-grid">
            <div class="classify-card" v-for="cls in classifications" :key="cls.dimension">
              <h4>{{ cls.dimension }}</h4>
              <div class="classify-items">
                <div class="classify-item" v-for="item in cls.items" :key="item.name">
                  <span class="cls-name">{{ item.name }}</span>
                  <span class="cls-desc">{{ item.desc }}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第三章 -->
        <section id="section3" class="chapter">
          <h2>三、指标定义标准</h2>
          <p>每个指标都应该有一份"身份证"——完整、准确、无歧义的定义。以下是指标定义的标准字段模板。</p>

          <div class="definition-table">
            <table>
              <thead>
                <tr>
                  <th>字段名称</th>
                  <th>说明</th>
                  <th>示例（月度营收）</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="field in definitionFields" :key="field.name">
                  <td class="field-name">{{ field.name }}</td>
                  <td>{{ field.desc }}</td>
                  <td>{{ field.example }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">🎯</div>
              <div>
                <h4>指标定义黄金法则</h4>
                <p>一个好的指标定义应该让任何人——无论是CEO还是实习生——在不咨询任何人的情况下，仅通过阅读定义就能准确理解这个指标的含义并正确使用。</p>
              </div>
            </div>
          </div>
        </section>

        <!-- 第四章 -->
        <section id="section4" class="chapter">
          <h2>四、指标口径统一方法论</h2>

          <h3>4.1 口径不一致的典型场景</h3>
          <div class="scenario-cards">
            <div class="scenario-card" v-for="s in scenarios" :key="s.indicator">
              <div class="scenario-header">
                <span class="scenario-indicator">{{ s.indicator }}</span>
              </div>
              <div class="scenario-body">
                <div class="scenario-versions">
                  <div class="version-item" v-for="(v, i) in s.versions" :key="i">
                    <span class="version-dept">{{ v.dept }}</span>
                    <span class="version-def">{{ v.def }}</span>
                  </div>
                </div>
                <div class="scenario-impact">⚠️ {{ s.impact }}</div>
              </div>
            </div>
          </div>

          <h3>4.2 口径统一五步法</h3>
          <div class="unify-steps">
            <div class="unify-step" v-for="(step, i) in unifySteps" :key="i">
              <div class="unify-number" :style="{ background: step.color }">{{ i + 1 }}</div>
              <div class="unify-content">
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
              </div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>口径治理的关键：建立指标Owner制度</h4>
              <p>每个指标必须有且仅有一个"Owner"（通常是业务部门负责人），负责定义口径、裁决争议、审批变更。没有Owner的指标，口径永远对不齐。</p>
            </div>
          </div>
        </section>

        <!-- 第五章 -->
        <section id="section5" class="chapter">
          <h2>五、指标字典模板</h2>

          <p>指标字典是指标体系的"数据库"，记录每个指标的完整定义、口径、数据来源、计算逻辑等信息。</p>

          <h3>5.1 指标字典框架</h3>
          <div class="dict-framework">
            <div class="dict-module" v-for="mod in dictModules" :key="mod.name">
              <div class="dict-module-icon" :style="{ background: mod.color }">{{ mod.icon }}</div>
              <div>
                <h4>{{ mod.name }}</h4>
                <p>{{ mod.desc }}</p>
                <div class="dict-fields">
                  <span v-for="f in mod.fields" :key="f">{{ f }}</span>
                </div>
              </div>
            </div>
          </div>

          <h3>5.2 指标字典示例</h3>
          <div class="dict-example">
            <div class="dict-row" v-for="row in dictExample" :key="row.field">
              <span class="dict-field">{{ row.field }}</span>
              <span class="dict-value">{{ row.value }}</span>
            </div>
          </div>
        </section>

        <!-- 第六章 -->
        <section id="section6" class="chapter">
          <h2>六、指标血缘管理</h2>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">🔗</div>
              <div>
                <h4>什么是指标血缘</h4>
                <p>指标血缘（Metric Lineage）描述了指标从原始数据到最终展现的全链路关系——数据从哪里来、经过什么计算加工、呈现在哪些报表和应用中。</p>
              </div>
            </div>
          </div>

          <h3>6.1 血缘管理的价值</h3>
          <div class="value-grid">
            <div class="value-card" v-for="v in lineageValues" :key="v.title">
              <div class="value-icon">{{ v.icon }}</div>
              <h4>{{ v.title }}</h4>
              <p>{{ v.desc }}</p>
            </div>
          </div>

          <h3>6.2 血缘关系模型</h3>
          <div class="lineage-flow">
            <div class="lineage-node" v-for="(node, i) in lineageNodes" :key="i">
              <div class="lineage-node-inner" :style="{ borderColor: node.color }">
                <div class="lineage-label">{{ node.label }}</div>
                <div class="lineage-items">
                  <span v-for="item in node.items" :key="item">{{ item }}</span>
                </div>
              </div>
              <div v-if="i < lineageNodes.length - 1" class="lineage-arrow">→</div>
            </div>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、指标运营与持续优化</h2>

          <h3>7.1 指标运营核心流程</h3>
          <div class="ops-flow">
            <div class="ops-card" v-for="op in opsFlow" :key="op.title">
              <div class="ops-icon">{{ op.icon }}</div>
              <h4>{{ op.title }}</h4>
              <p>{{ op.desc }}</p>
            </div>
          </div>

          <h3>7.2 指标健康度评估</h3>
          <div class="health-table">
            <table>
              <thead>
                <tr>
                  <th>评估维度</th>
                  <th>评估内容</th>
                  <th>健康标准</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="h in healthMetrics" :key="h.dim">
                  <td class="health-dim">{{ h.dim }}</td>
                  <td>{{ h.content }}</td>
                  <td>{{ h.standard }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="visual-block blue-gradient" style="margin-top: 32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">📝</div>
              <div>
                <h4>结语</h4>
                <p>指标体系是企业数据驱动决策的"基础设施"。它不是一个IT项目，而是一场业务与技术协同的变革。从指标分层到口径统一，从字典管理到血缘追踪——每一步都在让企业的"数据语言"变得更加精确、统一、可信。</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>需要指标体系建设咨询？</h2>
        <p>古杉数据帮助企业构建"一个数、一个口径、一份报表"的指标管理体系</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约指标体系专家</router-link>
          <router-link to="/consulting/data-governance" class="btn-outline">了解数据治理咨询</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'MetricsSystem',
  data() {
    return {
      pyramidLevels: [
        { name: '战略层指标', desc: '反映企业战略目标达成情况，供CEO/董事会使用', color: 'linear-gradient(135deg, #0E1F52, #1F3F8F)', examples: ['营业收入', '净利润', '市场份额', 'ROE', '客户满意度'] },
        { name: '战术层指标', desc: '部门级运营目标，供VP/总监使用', color: 'linear-gradient(135deg, #1F6FEB, #4A90D9)', examples: ['获客成本', '客单价', '产能利用率', '人均产值', '库存周转率'] },
        { name: '运营层指标', desc: '一线执行指标，供经理/员工使用', color: 'linear-gradient(135deg, #4A90D9, #93C5FD)', examples: ['日活用户数', '工单处理时长', '来料合格率', '发货准时率'] }
      ],
      classifications: [
        { dimension: '按业务域', items: [{ name: '财务指标', desc: '营收/利润/成本' }, { name: '运营指标', desc: '效率/质量/交付' }, { name: '客户指标', desc: '满意度/留存/NPS' }, { name: '人力指标', desc: '人效/流失率/培训' }] },
        { dimension: '按计算方式', items: [{ name: '原子指标', desc: '不可再拆分的基础度量' }, { name: '派生指标', desc: '原子指标 + 维度限定' }, { name: '复合指标', desc: '多个指标的运算组合' }] },
        { dimension: '按时效性', items: [{ name: '实时指标', desc: '秒/分钟级更新' }, { name: '准实时指标', desc: '小时级更新' }, { name: '离线指标', desc: '日/周/月级更新' }] }
      ],
      definitionFields: [
        { name: '指标编码', desc: '全局唯一编码', example: 'FIN-REV-001' },
        { name: '指标名称', desc: '指标的规范中文名称', example: '月度营业收入' },
        { name: '指标英文名', desc: '英文标识，用于系统对接', example: 'monthly_revenue' },
        { name: '业务定义', desc: '用业务语言描述含义', example: '当月确认收入的主营业务收入总额' },
        { name: '计算公式', desc: '明确的数学公式', example: '∑(已确认收入的订单金额)' },
        { name: '口径说明', desc: '包含/排除的范围', example: '含税收入，排除关联交易冲减' },
        { name: '计量单位', desc: '数值单位', example: '万元（人民币）' },
        { name: '统计周期', desc: '时间粒度', example: '自然月（1日至月末）' },
        { name: '数据来源', desc: '原始数据所在系统', example: 'ERP-财务模块-收入确认表' },
        { name: '指标Owner', desc: '口径责任人', example: '财务部-收入核算组长' },
        { name: '刷新频率', desc: '指标更新频率', example: 'T+1日 08:00' }
      ],
      scenarios: [
        {
          indicator: '营业收入',
          versions: [
            { dept: '财务部', def: '已确认收入（权责发生制）' },
            { dept: '销售部', def: '已签合同金额（含未履约）' },
            { dept: '管理层', def: '含预付款的实际到账金额' }
          ],
          impact: '量级差异可达30%，导致经营分析会上数据打架'
        },
        {
          indicator: '客户数',
          versions: [
            { dept: '市场部', def: '注册用户数（含未激活）' },
            { dept: '运营部', def: '30天内有交互的活跃客户' },
            { dept: '财务部', def: '有回款记录的付费客户' }
          ],
          impact: '三个部门的"客户数"报表差异超过10倍'
        }
      ],
      unifySteps: [
        { title: '盘点全量指标', desc: '收集各部门、各系统中使用的所有指标，建立指标清单。', color: '#4A90D9' },
        { title: '识别冲突指标', desc: '通过名称比对、语义分析找出同名不同义、同义不同名的指标。', color: '#1F6FEB' },
        { title: '业务确认口径', desc: '召集指标Owner和相关部门，讨论确认唯一官方口径。', color: '#0E4EAD' },
        { title: '发布指标标准', desc: '将统一口径录入指标字典，正式发布为企业标准。', color: '#0A3A82' },
        { title: '系统落地执行', desc: '在BI/数仓中按统一口径实现指标计算，退役旧口径。', color: '#0E1F52' }
      ],
      dictModules: [
        { icon: '📋', name: '基本信息', desc: '指标编码、名称、分类、层级等基础属性', fields: ['指标编码', '指标名称', '所属域', '指标层级', '指标类型'], color: '#1A73E8' },
        { icon: '📐', name: '业务规则', desc: '口径定义、计算公式、统计维度等业务规则', fields: ['业务定义', '计算公式', '口径说明', '统计周期', '计量单位'], color: '#10B981' },
        { icon: '🔗', name: '技术信息', desc: '数据来源、加工逻辑、存储位置等技术信息', fields: ['源表', '加工逻辑', '目标表', '刷新频率', 'ETL任务'], color: '#F59E0B' },
        { icon: '👤', name: '管理信息', desc: '责任人、审批流程、变更记录等管理信息', fields: ['指标Owner', '审批人', '创建日期', '变更记录', '使用报表'], color: '#8B5CF6' }
      ],
      dictExample: [
        { field: '指标编码', value: 'FIN-REV-001' },
        { field: '指标名称', value: '月度营业收入' },
        { field: '所属域', value: '财务域' },
        { field: '指标层级', value: '战略层' },
        { field: '指标类型', value: '原子指标' },
        { field: '业务定义', value: '当月按权责发生制确认的主营业务收入总额' },
        { field: '计算公式', value: '∑(收入确认表.确认金额) WHERE 确认月份=统计月份' },
        { field: '口径说明', value: '含增值税；排除关联交易冲减项；不含政府补贴' },
        { field: '数据来源', value: 'ERP系统 > 财务模块 > 收入确认表' },
        { field: '指标Owner', value: '财务部-收入核算组长-张XX' },
        { field: '刷新频率', value: 'T+1日 08:00自动刷新' }
      ],
      lineageValues: [
        { icon: '🔍', title: '影响分析', desc: '数据源变更时，快速定位受影响的指标和报表' },
        { icon: '🐛', title: '问题定位', desc: '指标数据异常时，沿血缘链追溯到问题源头' },
        { icon: '📊', title: '口径审计', desc: '验证指标计算是否符合定义口径' },
        { icon: '🔄', title: '变更管理', desc: 'ETL逻辑变更前评估影响范围' }
      ],
      lineageNodes: [
        { label: '数据源', items: ['ERP', 'CRM', 'MES', '外部数据'], color: '#4A90D9' },
        { label: '数据集成', items: ['数据采集', '数据清洗', '数据转换'], color: '#1F6FEB' },
        { label: '数据仓库', items: ['ODS层', 'DWD层', 'DWS层', 'ADS层'], color: '#0E4EAD' },
        { label: '指标计算', items: ['原子指标', '派生指标', '复合指标'], color: '#0A3A82' },
        { label: '应用展现', items: ['BI看板', '管理报表', '数据产品'], color: '#0E1F52' }
      ],
      opsFlow: [
        { icon: '📝', title: '指标申请', desc: '业务部门提交新增/变更申请，说明背景和需求' },
        { icon: '🔍', title: '查重审核', desc: '指标管理员检查是否已有相同定义的指标' },
        { icon: '✅', title: '口径审批', desc: '指标Owner审批确认口径定义' },
        { icon: '🛠️', title: '技术实现', desc: '数据团队按口径开发ETL和计算逻辑' },
        { icon: '📊', title: '发布上线', desc: '指标录入字典，接入BI平台，对外发布' },
        { icon: '🔄', title: '定期检查', desc: '季度review指标使用率、准确率、时效性' }
      ],
      healthMetrics: [
        { dim: '覆盖率', content: '核心业务场景的指标覆盖程度', standard: '核心指标覆盖率 ≥ 95%' },
        { dim: '一致性', content: '不同报表中同名指标的口径一致性', standard: '冲突指标占比 ≤ 2%' },
        { dim: '使用率', content: '指标字典中指标被实际使用的比例', standard: '僵尸指标占比 ≤ 10%' },
        { dim: '时效性', content: '指标数据按时更新的比例', standard: 'SLA达标率 ≥ 98%' },
        { dim: '满意度', content: '业务用户对指标服务的满意度', standard: '满意度评分 ≥ 4.0/5.0' }
      ]
    }
  }
}
</script>

<style scoped>
.article-page { background: #fff; min-height: 100vh; }

.breadcrumb-nav { max-width: 960px; margin: 0 auto; padding: 20px 24px; font-size: 14px; color: #5A5E66; }
.breadcrumb-nav a { color: #1A73E8; text-decoration: none; }
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

.container { max-width: 960px; margin: 0 auto; padding: 0 24px 60px; }

.article-header { text-align: center; padding: 40px 0 32px; border-bottom: 1px solid #E8ECF0; margin-bottom: 40px; }
.article-header .article-tags { display: flex; justify-content: center; gap: 8px; margin-bottom: 20px; }
.article-header .tag { background: #EBF3FE; color: #1A73E8; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
.article-title { font-size: 2.2rem; font-weight: 800; color: #0E1F52; line-height: 1.3; margin-bottom: 16px; }
.article-subtitle { font-size: 1.1rem; color: #5A5E66; margin-bottom: 20px; }
.article-meta { display: flex; justify-content: center; gap: 24px; font-size: 14px; color: #8C8C8C; }

.intro-card { display: flex; gap: 16px; background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-left: 4px solid #1A73E8; border-radius: 12px; padding: 24px; margin-bottom: 40px; }
.intro-card .intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; }

.toc { background: #F6F8FC; border-radius: 12px; padding: 24px 28px; margin-bottom: 48px; }
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a { color: #1A73E8; text-decoration: none; font-size: 15px; }
.toc a:hover { text-decoration: underline; }

.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 { font-size: 1.6rem; font-weight: 800; color: #0E1F52; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #1A73E8; display: inline-block; }
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

.visual-block { border-radius: 12px; padding: 24px; margin: 24px 0; }
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 28px 0; }
.stat-card { background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #D6E4F0; }
.stat-number { font-size: 1.8rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

.highlight-box { display: flex; gap: 16px; background: #FFF7E6; border-left: 4px solid #FAAD14; border-radius: 12px; padding: 24px; margin: 28px 0; }
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 对比 */
.pain-comparison { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 24px 0; }
.pain-col { border-radius: 12px; padding: 24px; }
.pain-col.before { background: #FEF2F2; border: 1px solid #FECACA; }
.pain-col.after { background: #F0FFF4; border: 1px solid #D1FAE5; }
.pain-col h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.pain-col ul { list-style: none; padding: 0; margin: 0; }
.pain-col li { font-size: 14px; color: #3D4150; padding: 6px 0 6px 18px; position: relative; }
.pain-col.before li::before { content: '✗'; position: absolute; left: 0; color: #DC2626; font-weight: 700; }
.pain-col.after li::before { content: '✓'; position: absolute; left: 0; color: #10B981; font-weight: 700; }

/* 金字塔 */
.pyramid { margin: 24px auto; max-width: 700px; }
.pyramid-level { border-radius: 12px; padding: 24px; margin-bottom: 8px; color: #fff; }
.pyramid-content h4 { font-size: 18px; font-weight: 800; margin-bottom: 6px; }
.pyramid-content p { font-size: 14px; opacity: 0.85; margin-bottom: 10px; }
.pyramid-examples { display: flex; gap: 8px; flex-wrap: wrap; }
.pyramid-examples span { background: rgba(255,255,255,0.2); padding: 4px 12px; border-radius: 6px; font-size: 12px; }

/* 分类 */
.classify-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.classify-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.classify-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.classify-item { display: flex; gap: 8px; font-size: 13px; margin-bottom: 8px; }
.cls-name { font-weight: 600; color: #1A73E8; white-space: nowrap; }
.cls-desc { color: #5A5E66; }

/* 定义表 */
.definition-table { margin: 24px 0; overflow-x: auto; }
.definition-table table { width: 100%; border-collapse: collapse; font-size: 14px; }
.definition-table th { background: #0E1F52; color: #fff; padding: 12px 16px; text-align: left; font-weight: 600; }
.definition-table td { padding: 12px 16px; border-bottom: 1px solid #E8ECF0; color: #3D4150; }
.definition-table tr:nth-child(even) td { background: #F6F8FC; }
.field-name { font-weight: 700; color: #0E1F52; }

/* 场景 */
.scenario-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 24px 0; }
.scenario-card { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; }
.scenario-header { background: #0E1F52; padding: 14px 20px; }
.scenario-indicator { color: #fff; font-size: 16px; font-weight: 700; }
.scenario-body { padding: 20px; }
.version-item { display: flex; gap: 10px; font-size: 13px; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #E8ECF0; }
.version-item:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
.version-dept { font-weight: 600; color: #1A73E8; min-width: 60px; }
.version-def { color: #5A5E66; }
.scenario-impact { font-size: 13px; color: #DC2626; margin-top: 12px; font-weight: 500; }

/* 统一步骤 */
.unify-steps { position: relative; padding-left: 40px; margin: 24px 0; }
.unify-steps::before { content: ''; position: absolute; left: 19px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #4A90D9, #0E1F52); border-radius: 2px; }
.unify-step { position: relative; margin-bottom: 20px; }
.unify-step:last-child { margin-bottom: 0; }
.unify-number { position: absolute; left: -40px; width: 38px; height: 38px; border-radius: 50%; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; z-index: 1; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.unify-content { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.unify-content h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.unify-content p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* 字典框架 */
.dict-framework { margin: 24px 0; }
.dict-module { display: flex; gap: 16px; background: #F6F8FC; border-radius: 12px; padding: 20px; margin-bottom: 12px; border: 1px solid #E8ECF0; }
.dict-module-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; flex-shrink: 0; color: #fff; }
.dict-module h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.dict-module > div > p { font-size: 14px; color: #5A5E66; margin-bottom: 8px; }
.dict-fields { display: flex; gap: 8px; flex-wrap: wrap; }
.dict-fields span { background: #EBF3FE; color: #1A73E8; padding: 3px 10px; border-radius: 6px; font-size: 12px; }

/* 字典示例 */
.dict-example { background: #F6F8FC; border-radius: 12px; padding: 4px; margin: 24px 0; border: 1px solid #E8ECF0; }
.dict-row { display: flex; padding: 10px 16px; border-bottom: 1px solid #E8ECF0; }
.dict-row:last-child { border-bottom: none; }
.dict-field { width: 120px; flex-shrink: 0; font-size: 14px; font-weight: 700; color: #0E1F52; }
.dict-value { font-size: 14px; color: #3D4150; }

/* 血缘 */
.value-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.value-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; }
.value-icon { font-size: 2rem; margin-bottom: 8px; }
.value-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.value-card p { font-size: 13px; color: #5A5E66; margin: 0; }

.lineage-flow { display: flex; align-items: center; margin: 24px 0; overflow-x: auto; gap: 0; }
.lineage-node { display: flex; align-items: center; }
.lineage-node-inner { border: 2px solid; border-radius: 12px; padding: 16px; min-width: 140px; text-align: center; }
.lineage-label { font-size: 13px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.lineage-items { display: flex; flex-direction: column; gap: 4px; }
.lineage-items span { font-size: 12px; color: #5A5E66; }
.lineage-arrow { font-size: 1.5rem; color: #1A73E8; margin: 0 8px; font-weight: 700; }

/* 运营 */
.ops-flow { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.ops-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; }
.ops-icon { font-size: 2rem; margin-bottom: 8px; }
.ops-card h4 { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.ops-card p { font-size: 13px; color: #5A5E66; margin: 0; }

/* 健康表 */
.health-table { margin: 24px 0; overflow-x: auto; }
.health-table table { width: 100%; border-collapse: collapse; font-size: 14px; }
.health-table th { background: #0E1F52; color: #fff; padding: 12px 16px; text-align: left; font-weight: 600; }
.health-table td { padding: 12px 16px; border-bottom: 1px solid #E8ECF0; color: #3D4150; }
.health-table tr:nth-child(even) td { background: #F6F8FC; }
.health-dim { font-weight: 700; color: #0E1F52; }

/* CTA */
.cta-section { text-align: center; background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; border-radius: 16px; padding: 48px 32px; margin-bottom: 60px; }
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 16px; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary { background: #1A73E8; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; transition: all 0.3s; }
.btn-primary:hover { background: #1557B0; }
.btn-outline { background: transparent; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; border: 2px solid rgba(255,255,255,0.5); transition: all 0.3s; }
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

@media (max-width: 768px) {
  .article-title { font-size: 1.6rem; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .pain-comparison { grid-template-columns: 1fr; }
  .classify-grid { grid-template-columns: 1fr; }
  .scenario-cards { grid-template-columns: 1fr; }
  .value-grid { grid-template-columns: repeat(2, 1fr); }
  .ops-flow { grid-template-columns: 1fr; }
  .lineage-flow { flex-direction: column; }
  .lineage-arrow { transform: rotate(90deg); }
  .cta-buttons { flex-direction: column; align-items: center; }
}
@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
  .value-grid { grid-template-columns: 1fr; }
}
</style>
